import { useEffect, useMemo, useState } from 'react'
import { Loader2, Search, UsersRound, GraduationCap, UserMinus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { ScrollArea } from '@/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useToast } from '@/hooks/use-toast'
import { useAdminUsers } from '@/services/admin/users.service'
import { useCompanies } from '@/services/admin/companies.service'
import {
  useAdminCourses,
  useBulkUnenroll,
  useCourseEnrollments,
} from '@/services/admin/courses.service'
import { getErrorMessage } from '@/utils/error'

interface BulkUnenrollDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Descadastro em Massa — inverso do `BulkEnrollDialog`. Super_admin
 * escolhe empresa + curso, vê **somente usuários já matriculados**
 * naquele curso, marca quem quer descadastrar e confirma via
 * AlertDialog.
 *
 * Diferenças-chave vs BulkEnrollDialog:
 * - Lista mostra apenas matriculados (cruzando users da empresa
 *   com `course_enrollments` via `useCourseEnrollments`)
 * - NENHUM user pré-selecionado (decisão D5 — operação destrutiva
 *   pede ação explícita)
 * - AlertDialog de confirmação antes de chamar a mutation (D4)
 * - Só remove a matrícula. Progresso individual, quiz attempts e
 *   feedback ficam intactos — consistente com o descadastro single
 */
export function BulkUnenrollDialog({ open, onOpenChange }: BulkUnenrollDialogProps) {
  const { toast } = useToast()
  const [companyId, setCompanyId] = useState('')
  const [courseId, setCourseId] = useState('')
  const [search, setSearch] = useState('')
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [confirmOpen, setConfirmOpen] = useState(false)

  const numericCourseId = courseId ? Number(courseId) : null

  const { data: companies } = useCompanies(open)
  const { data: courses } = useAdminCourses()
  const { data: users, isLoading: usersLoading } = useAdminUsers(undefined, companyId || undefined)
  const { data: enrollments, isLoading: enrollmentsLoading } = useCourseEnrollments(numericCourseId)

  const bulkUnenroll = useBulkUnenroll()
  const isPending = bulkUnenroll.isPending

  // Cursos elegíveis: publicados e da empresa escolhida (ou globais).
  const courseOptions = useMemo(() => {
    if (!companyId) return []
    return (courses ?? []).filter(
      (c) => c.status === 'published' && (c.companyId == null || c.companyId === companyId)
    )
  }, [courses, companyId])

  // Set de quem está matriculado — base pra filtrar a lista mostrada.
  const enrolledSet = useMemo(
    () => new Set((enrollments ?? []).map((e) => e.userId)),
    [enrollments]
  )

  // Lista mostrada: APENAS users da empresa que estão matriculados (alvos
  // potenciais do descadastro). Inverso do BulkEnrollDialog.
  const enrolledUsers = useMemo(
    () => (users ?? []).filter((u) => enrolledSet.has(u.id)),
    [users, enrolledSet]
  )

  // Reset da seleção quando a lista mudar (mudou empresa/curso/dados).
  // NÃO pré-seleciona ninguém — D5.
  useEffect(() => {
    setSelectedUserIds(new Set())
  }, [enrolledUsers])

  const bothChosen = Boolean(companyId && courseId)
  const listLoading = usersLoading || enrollmentsLoading

  // A busca filtra apenas o que é renderizado — não mexe na seleção.
  const visibleUsers = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return enrolledUsers
    return enrolledUsers.filter(
      (u) => u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q)
    )
  }, [enrolledUsers, search])

  const allSelected =
    enrolledUsers.length > 0 && enrolledUsers.every((u) => selectedUserIds.has(u.id))

  const handleCompanyChange = (value: string) => {
    setCompanyId(value)
    setCourseId('')
    setSelectedUserIds(new Set())
    setSearch('')
  }

  const handleCourseChange = (value: string) => {
    setCourseId(value)
    setSearch('')
  }

  const toggleAll = (checked: boolean) => {
    setSelectedUserIds(checked ? new Set(enrolledUsers.map((u) => u.id)) : new Set())
  }

  const toggleOne = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  // Trava: não fecha enquanto a operação está em andamento.
  const handleOpenChange = (next: boolean) => {
    if (isPending) return
    onOpenChange(next)
  }

  const courseTitle =
    courseOptions.find((c) => String(c.id) === courseId)?.title ?? 'curso selecionado'

  /** Após o "Descadastrar" no dialog principal: abre confirmação. */
  const handleSubmitClick = () => {
    if (!numericCourseId || selectedUserIds.size === 0) return
    setConfirmOpen(true)
  }

  /** Confirmação do AlertDialog → de fato chama a API. */
  const handleConfirm = async () => {
    if (!numericCourseId) return

    try {
      const result = await bulkUnenroll.mutateAsync({
        courseId: numericCourseId,
        userIds: [...selectedUserIds],
      })

      const parts = [`${result.unenrolled} usuário(s) descadastrado(s)`]
      if (result.notEnrolled > 0) parts.push(`${result.notEnrolled} não estavam matriculado(s)`)
      if (result.skipped > 0) parts.push(`${result.skipped} ignorado(s)`)

      toast({ title: 'Descadastro em massa concluído', description: parts.join(' · ') })
      setConfirmOpen(false)
      onOpenChange(false)
    } catch (error: unknown) {
      toast({
        variant: 'destructive',
        title: 'Erro no descadastro em massa',
        description: getErrorMessage(error, 'Não foi possível concluir o descadastro.'),
      })
      setConfirmOpen(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-[620px] overflow-hidden"
        onEscapeKeyDown={(e) => isPending && e.preventDefault()}
        onInteractOutside={(e) => isPending && e.preventDefault()}
        onPointerDownOutside={(e) => isPending && e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
              <UserMinus className="h-5 w-5" />
            </div>
            <div>
              <DialogTitle>Descadastro em Massa</DialogTitle>
              <DialogDescription>
                Remova a matrícula de vários usuários de um curso de uma vez.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4">
          {/* Empresa + Curso */}
          <div className="grid gap-3">
            <div className="space-y-1.5">
              <label className="text-sm font-medium">Empresa</label>
              <Select value={companyId} onValueChange={handleCompanyChange} disabled={isPending}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a empresa" />
                </SelectTrigger>
                <SelectContent>
                  {companies
                    ?.filter((c) => c.active)
                    .map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium">Curso</label>
              <Select
                value={courseId}
                onValueChange={handleCourseChange}
                disabled={isPending || !companyId}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={companyId ? 'Selecione o curso' : 'Escolha a empresa primeiro'}
                  />
                </SelectTrigger>
                <SelectContent>
                  {courseOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      Nenhum curso publicado disponível
                    </div>
                  ) : (
                    courseOptions.map((c) => (
                      <SelectItem key={c.id} value={String(c.id)}>
                        {c.title}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Lista de matriculados */}
          <div className="rounded-lg border border-border/60">
            <div className="flex items-center gap-3 border-b border-border/60 p-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  placeholder="Buscar por nome ou email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="h-9 pl-9"
                  disabled={isPending || !bothChosen}
                />
              </div>
              {bothChosen && enrolledUsers.length > 0 && (
                <label className="flex shrink-0 items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={(c) => toggleAll(c === true)}
                    disabled={isPending}
                  />
                  Todos
                </label>
              )}
            </div>

            <ScrollArea className="h-[260px]">
              {!bothChosen ? (
                <div className="flex h-[260px] flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground">
                  <GraduationCap className="h-8 w-8 opacity-40" />
                  <p className="text-sm">
                    Selecione uma empresa e um curso para listar os matriculados.
                  </p>
                </div>
              ) : listLoading ? (
                <div className="flex h-[260px] items-center justify-center">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                </div>
              ) : visibleUsers.length === 0 ? (
                <div className="flex h-[260px] flex-col items-center justify-center gap-2 px-6 text-center text-muted-foreground">
                  <UsersRound className="h-8 w-8 opacity-40" />
                  <p className="text-sm">
                    {search
                      ? 'Nenhum usuário corresponde à busca.'
                      : 'Nenhum usuário matriculado nesta empresa neste curso.'}
                  </p>
                </div>
              ) : (
                <div className="p-1.5">
                  {visibleUsers.map((user) => {
                    const isSelected = selectedUserIds.has(user.id)

                    return (
                      <div
                        key={user.id}
                        onClick={() => !isPending && toggleOne(user.id)}
                        className={[
                          'flex items-center gap-3 rounded-md px-2.5 py-2 transition-colors cursor-pointer hover:bg-muted/60',
                          isSelected ? 'bg-destructive/5' : '',
                        ].join(' ')}
                      >
                        <Checkbox
                          checked={isSelected}
                          disabled={isPending}
                          onCheckedChange={() => toggleOne(user.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={user.avatar} />
                          <AvatarFallback className="text-xs">
                            {user.name.charAt(0).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{user.name}</p>
                          <p className="truncate text-xs text-muted-foreground">{user.email}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </ScrollArea>

            {/* Rodapé da lista: contagem */}
            {bothChosen && !listLoading && (
              <div className="border-t border-border/60 px-3 py-2 text-xs text-muted-foreground">
                <span className="font-medium text-foreground">{selectedUserIds.size}</span> de{' '}
                {enrolledUsers.length} matriculado(s) selecionados pra remover
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            onClick={handleSubmitClick}
            disabled={!bothChosen || selectedUserIds.size === 0 || isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Descadastrando...
              </>
            ) : (
              `Descadastrar (${selectedUserIds.size})`
            )}
          </Button>
        </DialogFooter>

        {/* Overlay de bloqueio durante a escrita no banco */}
        {isPending && (
          <div className="absolute inset-0 z-50 flex flex-col items-center justify-center gap-3 bg-background/85 backdrop-blur-sm">
            <Loader2 className="h-10 w-10 animate-spin text-destructive" />
            <p className="text-sm font-medium">Descadastrando usuários...</p>
            <p className="text-xs text-muted-foreground">
              Não feche esta janela enquanto processamos.
            </p>
          </div>
        )}
      </DialogContent>

      {/* Confirmação destrutiva — D4. Aparece antes de chamar a mutation. */}
      <AlertDialog open={confirmOpen} onOpenChange={(v) => !isPending && setConfirmOpen(v)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Descadastrar {selectedUserIds.size} usuário(s) do curso “{courseTitle}”?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação remove a matrícula dos usuários selecionados, mas mantém o progresso
              individual deles. Eles podem ser re-matriculados depois sem perder o histórico.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isPending}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirm}
              disabled={isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Sim, descadastrar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
